import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  FaTimes, FaSave, FaMoneyBillWave, FaTruck,
  FaCalendarAlt, FaTag, FaAlignLeft, FaPaperclip,
  FaRedoAlt, FaLightbulb, FaRobot, FaCheck,
  FaBookOpen, FaChevronDown, FaPlus,
  FaChevronLeft, FaChevronRight,
  FaFilePdf, FaEye, FaDownload, FaExternalLinkAlt,
  FaMobileAlt,
} from 'react-icons/fa';
import api from '../services/api';
import { toast } from 'react-toastify';
import './GastoModal.css';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const maskCurrency = (raw = '') => {
  const digits = String(raw).replace(/\D/g, '');
  if (!digits) return '';
  return (parseFloat(digits) / 100).toLocaleString('pt-BR', {
    style: 'currency', currency: 'BRL',
  });
};

const parseCurrency = (display = '') => {
  const digits = String(display).replace(/\D/g, '');
  return digits ? parseFloat(digits) / 100 : 0;
};

const buildInitial = (gasto) => {
  if (!gasto) {
    return {
      descricao: '', valorDisplay: '', valor: 0,
      data_vencimento: new Date().toISOString().split('T')[0],
      status: 'Pendente', categoria_id: '', veiculo_id: '',
      plano_contas_id: '', justificativa: '',
      isRecorrente: false, frequencia: 'mensal', parcelas: 12, anexo: null,
    };
  }
  const raw = gasto.valor
    ? String(parseFloat(gasto.valor).toFixed(2)).replace('.', '')
    : '';
  return {
    ...gasto,
    valorDisplay:    maskCurrency(raw),
    valor:           parseFloat(gasto.valor) || 0,
    data_vencimento: String(gasto.data_vencimento || '').split('T')[0],
    categoria_id:    gasto.categoria_id    || '',
    veiculo_id:      gasto.veiculo_id      || '',
    plano_contas_id: gasto.plano_contas_id || '',
    justificativa:   gasto.justificativa   || '',
    isRecorrente: false, frequencia: 'mensal', parcelas: 12, anexo: null,
  };
};

const CONFIANCA = {
  alta:  { label: 'Alta confiança',  color: '#065f46', bg: '#d1fae5', border: '#6ee7b7' },
  media: { label: 'Confiança média', color: '#92400e', bg: '#fef3c7', border: '#fcd34d' },
  baixa: { label: 'Baixa confiança', color: '#991b1b', bg: '#fee2e2', border: '#fca5a5' },
};

const PERFIS_ADMIN = ['admin_geral', 'admin'];

// ─── Helpers de Anexos ────────────────────────────────────────────────────────

/**
 * Detecta se uma URL/tipo é imagem.
 * Cobre URLs do Google Drive thumbnail, Drive webview e extensões comuns.
 */
const isImage = (url = '', tipo = '') => {
  if (tipo && tipo.startsWith('image/')) return true;
  const u = String(url).toLowerCase();
  if (u.includes('drive.google.com/thumbnail')) return true;
  if (u.includes('googleusercontent.com'))       return true;
  return /\.(jpg|jpeg|png|webp|gif|bmp|svg)(\?|$)/.test(u);
};

/**
 * Normaliza os anexos vindos do gasto (mobile ou legado):
 *   - gasto.anexos  → JSON array enviado pelo app (múltiplos)
 *   - gasto.anexo_url → campo legado do web (único)
 */
const parseAnexosGasto = (gasto) => {
  if (!gasto) return [];
  const result = [];

  if (gasto.anexos) {
    let list = gasto.anexos;
    if (typeof list === 'string') {
      try { list = JSON.parse(list); } catch { list = []; }
    }
    if (Array.isArray(list)) {
      list.forEach((a, i) => {
        const url  = typeof a === 'string' ? a : (a.url || a.uri || '');
        const nome = typeof a === 'string' ? `Foto ${i + 1}` : (a.nome || a.name || `Foto ${i + 1}`);
        const tipo = typeof a === 'string' ? '' : (a.tipo || a.mimeType || '');
        if (url) result.push({ url, nome, tipo });
      });
    }
  }

  // Anexo único legado — inclui apenas se ainda não está na lista
  if (gasto.anexo_url && !result.find((a) => a.url === gasto.anexo_url)) {
    result.push({ url: gasto.anexo_url, nome: 'Comprovante', tipo: '' });
  }

  return result;
};

// ─── Lightbox ─────────────────────────────────────────────────────────────────
function Lightbox({ anexos, indexInicial, onClose }) {
  const [idx, setIdx] = useState(indexInicial);
  const atual   = anexos[idx];
  const ehImg   = isImage(atual?.url, atual?.tipo);

  // Navegação por teclado
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape')     { onClose(); return; }
      if (e.key === 'ArrowLeft'  && idx > 0)                setIdx((i) => i - 1);
      if (e.key === 'ArrowRight' && idx < anexos.length - 1) setIdx((i) => i + 1);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [idx, anexos.length, onClose]);

  if (!atual) return null;

  return ReactDOM.createPortal(
    <div className="gm-lb-overlay" onClick={onClose}>
      <div className="gm-lb-box" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="gm-lb-header">
          <span className="gm-lb-title" title={atual.nome}>{atual.nome}</span>
          <div className="gm-lb-actions">
            <a
              href={atual.url} target="_blank" rel="noreferrer"
              className="gm-lb-btn" title="Abrir em nova aba"
            >
              <FaExternalLinkAlt />
            </a>
            <a
              href={atual.url} download={atual.nome}
              className="gm-lb-btn" title="Baixar"
            >
              <FaDownload />
            </a>
            <button className="gm-lb-btn gm-lb-btn--close" onClick={onClose} title="Fechar">
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Setas de navegação */}
        {idx > 0 && (
          <button
            className="gm-lb-nav gm-lb-nav--prev"
            onClick={() => setIdx((i) => i - 1)}
            title="Anterior"
          >
            <FaChevronLeft />
          </button>
        )}
        {idx < anexos.length - 1 && (
          <button
            className="gm-lb-nav gm-lb-nav--next"
            onClick={() => setIdx((i) => i + 1)}
            title="Próximo"
          >
            <FaChevronRight />
          </button>
        )}

        {/* Conteúdo */}
        <div className="gm-lb-content">
          {ehImg ? (
            <img src={atual.url} alt={atual.nome} className="gm-lb-img" />
          ) : (
            <div className="gm-lb-doc">
              <FaFilePdf size={72} color="#ef4444" />
              <p className="gm-lb-doc__name">{atual.nome}</p>
              <a
                href={atual.url} target="_blank" rel="noreferrer"
                className="gm-lb-doc__link"
              >
                <FaExternalLinkAlt /> Abrir documento
              </a>
            </div>
          )}
        </div>

        {/* Contador */}
        {anexos.length > 1 && (
          <div className="gm-lb-counter">
            {idx + 1} / {anexos.length}
          </div>
        )}
      </div>
    </div>,
    document.getElementById('modal-root') || document.body
  );
}

// ─── Visualizador de Anexos ───────────────────────────────────────────────────
function AnexosVisualizador({ anexos }) {
  const [lbIndex, setLbIndex] = useState(null);

  return (
    <>
      <div className="gm-field gm-anexos-section">
        <label className="gm-anexos-label">
          <span className="gm-anexos-label__left">
            <span className="gm-anexos-label__icon-wrap">
              <FaMobileAlt size={12} />
            </span>
            Anexos enviados pelo app
          </span>
          <span className="gm-anexos-count">{anexos.length}</span>
        </label>

        <div className="gm-anexos-grid">
          {anexos.map((a, i) => {
            const ehImg = isImage(a.url, a.tipo);
            return (
              <div
                key={i}
                className="gm-anexo-card"
                onClick={() => setLbIndex(i)}
                title={`Ver: ${a.nome}`}
              >
                {ehImg ? (
                  <img src={a.url} alt={a.nome} className="gm-anexo-card__img" />
                ) : (
                  <div className="gm-anexo-card__doc">
                    <FaFilePdf size={30} color="#ef4444" />
                    <span className="gm-anexo-card__doc-ext">PDF</span>
                  </div>
                )}

                {/* Overlay ao hover */}
                <div className="gm-anexo-card__overlay">
                  <FaEye size={20} color="#fff" />
                </div>

                {/* Legenda */}
                <span className="gm-anexo-card__label">{a.nome}</span>
              </div>
            );
          })}
        </div>

        <p className="gm-anexos-hint">
          Clique em qualquer imagem para ampliar · Use as setas ou teclado para navegar
        </p>
      </div>

      {lbIndex !== null && (
        <Lightbox
          anexos={anexos}
          indexInicial={lbIndex}
          onClose={() => setLbIndex(null)}
        />
      )}
    </>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function GastoModal({
  gasto,
  onClose,
  onSave,
  categorias,
  veiculos,
  userPerfil,
  onCategoriaCreated,
}) {
  const [form,         setForm]       = useState(() => buildInitial(gasto));
  const [isSubmitting, setSubmitting] = useState(false);
  const [errors,       setErrors]     = useState({});

  const [planoContas,      setPlanoContas]      = useState([]);
  const [iaSugestao,       setIaSugestao]       = useState(null);
  const [iaSuggesting,     setIaSuggesting]     = useState(false);
  const [contaSelecionada, setContaSelecionada] = useState(null);

  const [localCats,   setLocalCats]   = useState([]);
  const [showNewCat,  setShowNewCat]  = useState(false);
  const [newCatNome,  setNewCatNome]  = useState('');
  const [creatingCat, setCreatingCat] = useState(false);

  const canCreateCat = PERFIS_ADMIN.includes(userPerfil);

  // Anexos vindos do app (mobile) para exibição
  const anexosMobile = gasto ? parseAnexosGasto(gasto) : [];

  useEffect(() => { setLocalCats(categorias); }, [categorias]);

  useEffect(() => {
    api.get('/api/financeiro/plano-contas')
      .then((r) => setPlanoContas(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setForm(buildInitial(gasto));
    setErrors({});
    setIaSugestao(null);
    setShowNewCat(false);
    setNewCatNome('');
  }, [gasto]);

  useEffect(() => {
    if (form.plano_contas_id && planoContas.length > 0) {
      setContaSelecionada(
        planoContas.find((p) => String(p.id) === String(form.plano_contas_id)) || null
      );
    } else {
      setContaSelecionada(null);
    }
  }, [form.plano_contas_id, planoContas]);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked, files } = e.target;
    if (name === 'valorDisplay') {
      const d = value.replace(/\D/g, '');
      setForm((p) => ({ ...p, valorDisplay: maskCurrency(d), valor: parseCurrency(d) }));
      return;
    }
    setForm((p) => ({
      ...p,
      [name]: type === 'checkbox' ? checked : type === 'file' ? files[0] : value,
    }));
    if (['descricao', 'categoria_id'].includes(name)) setIaSugestao(null);
  }, []);

  const handleCreateCategoria = useCallback(async () => {
    const nome = newCatNome.trim();
    if (!nome) return;
    setCreatingCat(true);
    try {
      const { data } = await api.post('/api/gastos/categorias', { nome });
      const nova = { id: data.id, nome: data.nome, tenant_id: data.tenant_id };
      setLocalCats((prev) => [...prev, nova].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')));
      setForm((prev) => ({ ...prev, categoria_id: String(data.id) }));
      setErrors((prev) => ({ ...prev, categoria_id: undefined }));
      if (typeof onCategoriaCreated === 'function') onCategoriaCreated(nova);
      setNewCatNome('');
      setShowNewCat(false);
      toast.success(`✓ Categoria "${data.nome}" criada e selecionada!`);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Erro ao criar categoria.');
    } finally {
      setCreatingCat(false);
    }
  }, [newCatNome, onCategoriaCreated]);

  const handleSugerirConta = useCallback(async () => {
    if (!form.descricao.trim()) { toast.warn('Preencha a descrição antes de usar a sugestão.'); return; }
    setIaSuggesting(true);
    setIaSugestao(null);
    try {
      const categoriaNome = localCats.find((c) => String(c.id) === String(form.categoria_id))?.nome || '';
      const { data } = await api.post('/api/gastos/sugerir-conta', {
        descricao: form.descricao, categoria_nome: categoriaNome, valor: form.valor,
      });
      setIaSugestao(data);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'IA indisponível. Selecione manualmente.');
    } finally {
      setIaSuggesting(false);
    }
  }, [form.descricao, form.categoria_id, form.valor, localCats]);

  const aplicarSugestao = useCallback((s) => {
    setForm((p) => ({ ...p, plano_contas_id: String(s.id) }));
    setIaSugestao(null);
    toast.success(`✓ ${s.codigo} — ${s.nome}`);
  }, []);

  const validate = useCallback(() => {
    const e = {};
    if (!form.descricao.trim())         e.descricao       = 'Descrição obrigatória.';
    if (!form.valor || form.valor <= 0) e.valor           = 'Informe um valor maior que zero.';
    if (!form.data_vencimento)          e.data_vencimento = 'Data obrigatória.';
    if (!form.categoria_id)             e.categoria_id    = 'Selecione uma categoria.';
    return e;
  }, [form]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('descricao',       form.descricao);
      fd.append('valor',           form.valor);
      fd.append('data_vencimento', form.data_vencimento);
      fd.append('status',          form.status);
      fd.append('categoria_id',    form.categoria_id);
      fd.append('veiculo_id',      form.veiculo_id      || '');
      fd.append('plano_contas_id', form.plano_contas_id || '');
      fd.append('justificativa',   form.justificativa   || '');
      if (form.isRecorrente && !gasto) {
        fd.append('isRecorrente', 'true');
        fd.append('frequencia',   form.frequencia);
        fd.append('parcelas',     form.parcelas);
      }
      if (form.anexo) fd.append('anexo', form.anexo);
      await onSave(fd, gasto ? gasto.id : null);
    } finally {
      setSubmitting(false);
    }
  }, [form, gasto, onSave, validate]);

  const stopProp = useCallback((e) => e.stopPropagation(), []);
  const portal   = document.getElementById('modal-root') || document.body;

  return ReactDOM.createPortal(
    <div className="gm-overlay" onClick={onClose}>
      <div className="gm-modal" onClick={stopProp} role="dialog" aria-modal="true">

        {/* HEADER */}
        <div className="gm-header">
          <div className="gm-header__left">
            <div className="gm-header__icon"><FaMoneyBillWave /></div>
            <div>
              <h3>{gasto ? 'Editar Lançamento' : 'Novo Lançamento'}</h3>
              <p>{gasto ? `ID #${gasto.id}` : 'Preencha os dados abaixo'}</p>
            </div>
          </div>
          <button className="gm-close" onClick={onClose} title="Fechar"><FaTimes /></button>
        </div>

        {/* FORM */}
        <form className="gm-form" onSubmit={handleSubmit} noValidate>

          {/* Linha 1 — Descrição + Valor */}
          <div className="gm-row">
            <div className={`gm-field gm-field--grow ${errors.descricao ? 'gm-field--error' : ''}`}>
              <label><FaAlignLeft className="gm-field__icon" /> Descrição *</label>
              <input
                type="text" name="descricao" value={form.descricao}
                onChange={handleChange} placeholder="Ex: Combustível, Peças, Seguro…"
                autoFocus={!gasto}
              />
              {errors.descricao && <span className="gm-error-msg">{errors.descricao}</span>}
            </div>

            <div className={`gm-field ${errors.valor ? 'gm-field--error' : ''}`}>
              <label><FaMoneyBillWave className="gm-field__icon" /> Valor *</label>
              <input
                type="text" name="valorDisplay" value={form.valorDisplay}
                onChange={handleChange} placeholder="R$ 0,00"
                className="gm-input--money" inputMode="numeric"
              />
              {errors.valor && <span className="gm-error-msg">{errors.valor}</span>}
            </div>
          </div>

          {/* Linha 2 — Data + Categoria */}
          <div className="gm-row">
            <div className={`gm-field ${errors.data_vencimento ? 'gm-field--error' : ''}`}>
              <label><FaCalendarAlt className="gm-field__icon" /> Vencimento *</label>
              <input type="date" name="data_vencimento" value={form.data_vencimento} onChange={handleChange} />
              {errors.data_vencimento && <span className="gm-error-msg">{errors.data_vencimento}</span>}
            </div>

            <div className={`gm-field gm-field--grow ${errors.categoria_id ? 'gm-field--error' : ''}`}>
              <label>
                <FaTag className="gm-field__icon" /> Categoria *
                {localCats.length === 0 && !canCreateCat && (
                  <span className="gm-field__hint"> — nenhuma cadastrada</span>
                )}
              </label>

              <div className="gm-cat-row">
                <select
                  name="categoria_id"
                  value={form.categoria_id}
                  onChange={handleChange}
                  className={localCats.length === 0 ? 'gm-select--empty' : ''}
                >
                  <option value="">
                    {localCats.length === 0 ? 'Nenhuma categoria — crie uma abaixo' : 'Selecione…'}
                  </option>
                  {localCats.map((c) => (
                    <option key={c.id} value={c.id}>{c.nome}</option>
                  ))}
                </select>

                {canCreateCat && (
                  <button
                    type="button"
                    className={`gm-cat-add ${showNewCat ? 'gm-cat-add--active' : ''}`}
                    onClick={() => { setShowNewCat((v) => !v); setNewCatNome(''); }}
                    title={showNewCat ? 'Cancelar' : 'Criar nova categoria'}
                  >
                    {showNewCat ? <FaTimes /> : <FaPlus />}
                  </button>
                )}
              </div>

              {showNewCat && (
                <div className="gm-new-cat">
                  <input
                    type="text"
                    className="gm-new-cat__input"
                    placeholder="Nome da nova categoria…"
                    value={newCatNome}
                    onChange={(e) => setNewCatNome(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter')  { e.preventDefault(); handleCreateCategoria(); }
                      if (e.key === 'Escape') { setShowNewCat(false); setNewCatNome(''); }
                    }}
                    autoFocus
                    maxLength={60}
                    disabled={creatingCat}
                  />
                  <button
                    type="button"
                    className="gm-new-cat__save"
                    onClick={handleCreateCategoria}
                    disabled={creatingCat || !newCatNome.trim()}
                  >
                    {creatingCat ? <span className="gm-spinner" /> : <FaCheck />}
                    {creatingCat ? 'Criando…' : 'Criar'}
                  </button>
                </div>
              )}

              {errors.categoria_id && (
                <span className="gm-error-msg">{errors.categoria_id}</span>
              )}
            </div>
          </div>

          {/* ── PLANO DE CONTAS ─────────────────────────────────────── */}
          <div className="gm-field">
            <label><FaBookOpen className="gm-field__icon" /> Conta do Plano de Contas</label>

            <div className="gm-conta-wrap">
              <div className="gm-conta-row">
                <div className="gm-select-wrap">
                  <select
                    name="plano_contas_id"
                    value={form.plano_contas_id}
                    onChange={handleChange}
                    className="gm-conta-select"
                  >
                    <option value="">
                      {planoContas.length === 0 ? 'Carregando…' : 'Selecionar manualmente…'}
                    </option>
                    {planoContas.map((c) => (
                      <option key={c.id} value={c.id}>{c.codigo} — {c.nome}</option>
                    ))}
                  </select>
                  <FaChevronDown className="gm-select-arrow" />
                </div>

                <button
                  type="button"
                  className={`gm-ia-trigger ${iaSuggesting ? 'gm-ia-trigger--busy' : ''}`}
                  onClick={handleSugerirConta}
                  disabled={iaSuggesting}
                  title={form.descricao.trim() ? 'Sugerir conta com IA' : 'Preencha a descrição primeiro'}
                >
                  {iaSuggesting ? <span className="gm-spinner" /> : <FaLightbulb />}
                  <span>{iaSuggesting ? 'Consultando…' : 'Sugerir com IA'}</span>
                </button>
              </div>

              {contaSelecionada && !iaSugestao && (
                <div className="gm-conta-chip">
                  <FaCheck className="gm-conta-chip__icon" />
                  <code>{contaSelecionada.codigo}</code>
                  <span>{contaSelecionada.nome}</span>
                </div>
              )}

              {iaSugestao && (
                <div className="gm-ia-card">
                  <div className="gm-ia-card__top">
                    <div className="gm-ia-card__label"><FaRobot /> Sugestão da IA</div>
                    <span
                      className="gm-ia-badge"
                      style={{
                        color:       (CONFIANCA[iaSugestao.confianca] || CONFIANCA.media).color,
                        background:  (CONFIANCA[iaSugestao.confianca] || CONFIANCA.media).bg,
                        borderColor: (CONFIANCA[iaSugestao.confianca] || CONFIANCA.media).border,
                      }}
                    >
                      {(CONFIANCA[iaSugestao.confianca] || CONFIANCA.media).label}
                    </span>
                  </div>
                  <div className="gm-ia-card__conta">
                    <code>{iaSugestao.codigo}</code>
                    <strong>{iaSugestao.nome}</strong>
                  </div>
                  {iaSugestao.justificativa && (
                    <p className="gm-ia-card__reason">"{iaSugestao.justificativa}"</p>
                  )}
                  <div className="gm-ia-card__actions">
                    <button type="button" className="gm-ia-card__apply" onClick={() => aplicarSugestao(iaSugestao)}>
                      <FaCheck /> Aplicar sugestão
                    </button>
                    <button type="button" className="gm-ia-card__dismiss" onClick={() => setIaSugestao(null)}>
                      Ignorar
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Linha 3 — Veículo + Status */}
          <div className="gm-row">
            <div className="gm-field gm-field--grow">
              <label><FaTruck className="gm-field__icon" /> Veículo (opcional)</label>
              <select name="veiculo_id" value={form.veiculo_id} onChange={handleChange}>
                <option value="">Despesa geral / administrativa</option>
                {veiculos.map((v) => (
                  <option key={v.id} value={v.id}>{v.placa} — {v.modelo}</option>
                ))}
              </select>
            </div>

            <div className="gm-field">
              <label>Status</label>
              <select
                name="status" value={form.status} onChange={handleChange}
                className={`gm-status-select gm-status--${form.status.toLowerCase()}`}
              >
                <option value="Pendente">Pendente</option>
                <option value="Aprovada">Aprovada</option>
                <option value="Paga">Paga</option>
              </select>
            </div>
          </div>

          {/* ── ANEXOS DO APP (mobile) ──────────────────────────────── */}
          {gasto && anexosMobile.length > 0 && (
            <AnexosVisualizador anexos={anexosMobile} />
          )}

          {/* Comprovante / Anexo (web — upload novo) */}
          <div className="gm-field">
            <label>
              <FaPaperclip className="gm-field__icon" />
              {gasto && anexosMobile.length > 0
                ? 'Adicionar / Substituir comprovante web'
                : 'Comprovante / Anexo'}
            </label>
            <div className="gm-file-area">
              <input type="file" id="gm-file" name="anexo" onChange={handleChange}
                className="gm-file-input" accept="image/*,application/pdf" />
              <label htmlFor="gm-file" className="gm-file-label">
                {form.anexo
                  ? form.anexo.name
                  : gasto?.anexo_url && anexosMobile.length === 0
                  ? '📎 Arquivo existente — clique para substituir'
                  : 'Clique para selecionar PDF ou imagem'}
              </label>
            </div>
          </div>

          {/* Justificativa */}
          <div className="gm-field">
            <label>Justificativa / Observação</label>
            <textarea name="justificativa" rows={2} value={form.justificativa}
              onChange={handleChange} placeholder="Informações adicionais…" />
          </div>

          {/* Recorrência */}
          {!gasto && (
            <div className="gm-recurrence">
              <label className="gm-recurrence__toggle">
                <input type="checkbox" name="isRecorrente" checked={form.isRecorrente} onChange={handleChange} />
                <span className="gm-recurrence__label"><FaRedoAlt /> Repetir esta despesa</span>
              </label>
              {form.isRecorrente && (
                <div className="gm-recurrence__options">
                  <div className="gm-field">
                    <label>Frequência</label>
                    <select name="frequencia" value={form.frequencia} onChange={handleChange}>
                      <option value="mensal">Mensal</option>
                      <option value="semanal">Semanal</option>
                      <option value="anual">Anual</option>
                    </select>
                  </div>
                  <div className="gm-field">
                    <label>Parcelas</label>
                    <input type="number" name="parcelas" value={form.parcelas}
                      onChange={handleChange} min={2} max={120} />
                  </div>
                  <div className="gm-recurrence__preview">{form.parcelas}× {form.frequencia}</div>
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="gm-footer">
            <button type="button" className="gm-btn gm-btn--ghost" onClick={onClose} disabled={isSubmitting}>
              Cancelar
            </button>
            <button type="submit" className="gm-btn gm-btn--primary" disabled={isSubmitting}>
              {isSubmitting
                ? <><span className="gm-spinner" /> Salvando…</>
                : <><FaSave /> {gasto ? 'Salvar Alterações' : 'Criar Lançamento'}</>
              }
            </button>
          </div>
        </form>
      </div>
    </div>,
    portal
  );
}